/**
 * `Canvas2DRenderer` — the only `Renderer` this phase ships.
 *
 * Everything it draws comes from `frame` (`render/types.ts`); the instance
 * itself holds nothing but the canvas, its 2d context and the resize guard.
 * `resize()` is `docs/KNOWN-BUGS.md` item 4 in practice: it always
 * recomputes the cell size, and only ever touches `canvas.width` /
 * `canvas.height` / `canvas.style` when `ResizeGuard` says something changed.
 */
import {
  activePieceCells,
  boxSize,
  DEFAULT_VISIBLE_ROWS,
  DEFAULT_WIDTH,
  ghostCells,
  peekNext,
  pieceCells,
} from '@interlock/engine';
import type { GameState, PieceKind } from '@interlock/engine';
import type { CellRect, Theme, ThemeTokens } from '@interlock/themes';

import { contrastRatio, DEFAULT_DARK_FOREGROUND } from './contrast.js';
import type { RingSnapshot } from './effects.js';
import { ResizeGuard } from './resize.js';
import type { Renderer, RenderFrame, RendererSize } from './types.js';

/** Extra columns of canvas width reserved to the right of the well for hold/next previews. */
const RAIL_COLUMNS = 5;
/**
 * The well border's own alpha — high, deliberately, so the play area reads
 * as a clear boundary rather than fading into the page chrome around it, on
 * every theme (`deriveWellInk` below is what makes that theme-aware).
 */
const WELL_BORDER_ALPHA = 0.9;
/**
 * The interior grid's own alpha — deliberately the opposite end of the scale
 * from the border above: "a TINY bit clearer... they must never compete with
 * pieces." A filled cell paints straight over its own boundary lines
 * (`paintWellGrid` runs before `paintBoard`), so this only ever has to read
 * against the *background*, never against a piece — which is why it can stay
 * this faint and still be visibly "a grid" rather than invisible.
 */
const WELL_GRID_ALPHA = 0.1;
const MINI_CELL_FRACTION = 0.62;
const MINI_BOX_CELLS = 4; // every piece's rotation box is at most 4x4 (the I piece)
/** How many upcoming pieces the NEXT queue shows. Unchanged from before this pass. */
const NEXT_PREVIEW_COUNT = 5;
/**
 * Every next-queue piece after the first is drawn at this fraction of a mini
 * cell, and at full brightness (`docs/decisions/0057`). It was 0.72 and
 * washed 40% toward the ground; on the pure black page the board's near-black
 * ground and the 2px keyline round every cell then swallowed most of a small
 * cell's colour, and the queue behind the first piece was hard to read. The
 * first piece keeps its own frame and the full size, so it still leads.
 */
const NEXT_SHRINK = 0.86;
/**
 * How far, as a fraction toward the rail's own background, the spent hold
 * slot fades: `0` is full brightness, `1` would be invisible. Consumed by
 * `paintMiniPiece`'s background wash (see its own comment for why a wash,
 * not `ctx.globalAlpha`, is what actually dims a piece here) *and* as a plain
 * `ctx.globalAlpha` for the label and frame, which are this renderer's own
 * draw calls and so have no theme to fight with. The whole hold slot fades by
 * this much once `holdAvailable` says it cannot be swapped again.
 */
const HOLD_USED_DIM = 0.6;

/**
 * The colour the well's own frame — its border and its interior grid — is
 * drawn in: whichever of a theme's foreground (or the shared dark-theme
 * fallback every other piece of chrome in this file already uses) and its
 * accent has the *higher* contrast against its background.
 *
 * `packages/themes` has zero diff this pass, so this reads only tokens every
 * theme already publishes rather than adding a seventh literal per theme
 * (`docs/decisions/0022`'s own reasoning for keeping meaning-bearing chrome
 * colours out of `ThemeTokens`, applied here to a *derived* one instead). For
 * all six current themes this lands on the foreground/fallback — a light
 * theme's ink and a dark theme's near-white both out-contrast their own
 * accent against their own background — but the comparison is real, not a
 * disguised constant: a future theme whose accent happens to contrast better
 * gets the correct frame colour automatically.
 *
 * Exported (rather than a private method) so it is unit-testable directly,
 * against a fake theme as well as the real six.
 */
export function deriveWellInk(tokens: ThemeTokens): string {
  const bg = tokens.background;
  const fg = tokens.foreground ?? DEFAULT_DARK_FOREGROUND;
  const accent = tokens.accent;
  return contrastRatio(fg, bg) >= contrastRatio(accent, bg) ? fg : accent;
}

export class Canvas2DRenderer implements Renderer {
  private readonly ctx: CanvasRenderingContext2D;
  private readonly guard = new ResizeGuard();

  constructor(private readonly canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d');
    if (ctx === null) throw new Error('Canvas2DRenderer: 2d context unavailable');
    this.ctx = ctx;
  }

  resize(size: RendererSize): void {
    const cols = size.cols ?? DEFAULT_WIDTH;
    const rows = size.rows ?? DEFAULT_VISIBLE_ROWS;
    const next = this.guard.apply({
      containerWidth: size.containerWidth,
      containerHeight: size.containerHeight,
      cols,
      rows,
      devicePixelRatio: size.devicePixelRatio,
      railColumns: RAIL_COLUMNS,
    });
    if (next === null) return; // Unchanged — KNOWN-BUGS 4. The canvas is never touched.

    this.canvas.width = next.bufferWidthPx;
    this.canvas.height = next.bufferHeightPx;
    this.canvas.style.width = `${next.widthPx}px`;
    this.canvas.style.height = `${next.heightPx}px`;
    // Every subsequent draw call works in CSS pixels; the transform is what
    // makes that produce a sharp image at the real device pixel ratio.
    this.ctx.setTransform(next.devicePixelRatio, 0, 0, next.devicePixelRatio, 0, 0);
  }

  render(frame: RenderFrame): void {
    const size = this.guard.size;
    if (size === null) return; // resize() has not been called with real dimensions yet.

    const { ctx } = this;
    const cellPx = size.cellPx;
    const board = frame.state.board;
    const slot = frame.slot ?? 0;
    const theme = frame.theme;

    const wellWidthPx = board.width * cellPx;
    const wellHeightPx = board.visibleRows * cellPx;

    // Draw coordinates are snapped to whole pixels (`cellPx` is already a
    // whole number — see `computeCellSize`), and the shake offset is rounded
    // too, so nothing here ever draws on a half pixel.
    const shakeX = Math.round(frame.effects.shakeX);
    const shakeY = Math.round(frame.effects.shakeY);

    ctx.save();
    ctx.clearRect(0, 0, size.widthPx, size.heightPx);
    // The whole stage — well AND rail — gets the board theme's own ground,
    // before the shake translate so it never moves. The page behind the
    // canvas is the app chrome, which has its own light/dark appearance and
    // knows nothing about the board theme (`docs/decisions/0044`): left
    // transparent, the rail's HOLD/NEXT labels and frames — drawn in the
    // theme's ink — sat on the chrome's ground, and a dark theme's light ink
    // on a light page (or Blueprint's dark ink on a dark one) was about
    // 1.1:1. Now the theme's ink is always on the theme's ground.
    // `widthPx`/`heightPx` are whole CSS pixels (`computeCellSize`), so this
    // fill is pixel-snapped, and `resize()`'s guard is untouched.
    ctx.fillStyle = theme.tokens.background;
    ctx.fillRect(0, 0, size.widthPx, size.heightPx);
    ctx.translate(shakeX, shakeY);

    ctx.fillStyle = theme.tokens.background;
    ctx.fillRect(0, 0, wellWidthPx, wellHeightPx);
    theme.paintWellBackground?.(ctx, { x: 0, y: 0, width: wellWidthPx, height: wellHeightPx });
    // Under the stack, same reason `paintWellBackground` is: a filled cell
    // paints straight over its own boundary lines, so the grid only has to
    // read against the empty background, never against a piece.
    this.paintWellGrid(theme, wellWidthPx, wellHeightPx, cellPx, board.width, board.visibleRows);

    this.paintBoard(theme, board, cellPx);
    this.paintClearingRows(theme, frame, board.width, cellPx);
    // FOG washes the stack `paintBoard` just drew — and only that — before
    // anything the player still needs to see (the ghost, the active piece) is
    // painted back on top of it. See `RenderFrame.fog`.
    if (frame.fog === true) this.paintFog(theme, wellWidthPx, wellHeightPx);

    if (frame.showGhost) this.paintGhost(theme, frame.state, slot, board.hiddenRows, cellPx);

    const partnerSlot = frame.partnerSlot;
    if (partnerSlot !== undefined && partnerSlot !== slot) {
      this.paintActivePiece(
        theme,
        frame.state,
        partnerSlot,
        board.hiddenRows,
        cellPx,
        frame.highContrast,
      );
      this.paintPartnerMark(theme, frame.state, partnerSlot, board.hiddenRows, cellPx);
    }

    this.paintActivePiece(theme, frame.state, slot, board.hiddenRows, cellPx, frame.highContrast);
    this.paintCorrection(frame, board.hiddenRows, cellPx);

    // The clear-burst particles and the quad/back-to-back rings: drawn last
    // before the frame, same as the correction cue above them — they are
    // transient material leaving the board, not part of the stack a piece
    // could be read against.
    this.paintParticles(frame, board.hiddenRows, cellPx, wellWidthPx, wellHeightPx);
    this.paintRing(frame.effects.quadRing, board.hiddenRows, cellPx, wellWidthPx, wellHeightPx);
    this.paintRing(frame.effects.accentRing, board.hiddenRows, cellPx, wellWidthPx, wellHeightPx);

    // Last, on top of everything: the well's own outer frame reads as a
    // clean edge rather than being interrupted by a piece or a clear flash
    // that reaches the boundary. It still shakes with the well (drawn before
    // `ctx.restore()` undoes the translate above) rather than sitting still
    // while the contents it frames move — the frame belongs to the well, not
    // to the page around it.
    this.paintWellBorder(theme, wellWidthPx, wellHeightPx, cellPx);

    ctx.restore();

    this.paintRail(theme, frame.state, slot, wellWidthPx, cellPx, frame.blindNext === true);
  }

  /**
   * FOG: wash the whole well in the theme's own `background` token, at an
   * opacity chosen to obscure rather than merely dim — this is drawn *before*
   * the ghost and the active piece, so nothing painted afterward is touched
   * by it. Using the theme's own background is what keeps this legible in
   * every theme, light or dark: it is the one colour a theme is obliged to
   * have made every piece readable against already, which is exactly what
   * "the stack has become unreadable" needs to look like — the same
   * mechanism `paintMiniPiece`'s dim wash and `paintPartnerMark`'s hatch
   * both already use, for the same reason.
   */
  private paintFog(theme: Theme, wellWidthPx: number, wellHeightPx: number): void {
    const { ctx } = this;
    ctx.save();
    ctx.globalAlpha = 0.86;
    ctx.fillStyle = theme.tokens.background;
    ctx.fillRect(0, 0, wellWidthPx, wellHeightPx);
    ctx.restore();
  }

  /**
   * The interior grid: one faint line at every internal column and row
   * boundary, so an empty well reads as a grid of cells rather than a blank
   * rectangle. The lines must never compete with pieces: `WELL_GRID_ALPHA` is
   * the number that keeps that true, and drawing this *before* `paintBoard`
   * is what makes it structural rather than merely low-alpha: a filled cell
   * paints straight over its own share of these lines, so a stack full of
   * pieces shows no grid at all where it matters least (there is already a
   * piece there to look at) and the full grid only where the well is empty.
   *
   * One `beginPath`/`stroke` pair for every line rather than one per line —
   * cheap, and it means every line shares the exact same `strokeStyle`/
   * `globalAlpha`/`lineWidth`, which is also what makes this easy to pin in
   * a test (one recorded stroke call, not `cols + rows`).
   *
   * Every coordinate is `n * cellPx + 0.5`: `cellPx` is already a whole
   * number (`computeCellSize`), so this centres a 1px line exactly on the
   * pixel boundary between two cells — the same crisp-line convention this
   * file already uses for the well border below.
   */
  private paintWellGrid(
    theme: Theme,
    wellWidthPx: number,
    wellHeightPx: number,
    cellPx: number,
    cols: number,
    rows: number,
  ): void {
    const { ctx } = this;
    ctx.save();
    ctx.strokeStyle = deriveWellInk(theme.tokens);
    ctx.globalAlpha = WELL_GRID_ALPHA;
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (let col = 1; col < cols; col++) {
      const x = col * cellPx + 0.5;
      ctx.moveTo(x, 0);
      ctx.lineTo(x, wellHeightPx);
    }
    for (let row = 1; row < rows; row++) {
      const y = row * cellPx + 0.5;
      ctx.moveTo(0, y);
      ctx.lineTo(wellWidthPx, y);
    }
    ctx.stroke();
    ctx.restore();
  }

  /**
   * The well's own outer frame: one stroke around the full outline, at `WELL_BORDER_ALPHA` (far stronger than the grid above,
   * deliberately — the two must never read as the same kind of line).
   *
   * `lineWidth` is rounded (`Math.round`) and the stroke is inset by exactly
   * half of it, so the line straddles the well's own edge symmetrically and
   * stays crisp regardless of whether the rounded width lands on an even or
   * an odd pixel count — the standard crisp-canvas-line technique, applied
   * here instead of the usual "+0.5 on an odd width" special case because
   * `inset` already *is* that half-pixel offset when `lineWidth` is odd.
   */
  private paintWellBorder(
    theme: Theme,
    wellWidthPx: number,
    wellHeightPx: number,
    cellPx: number,
  ): void {
    const { ctx } = this;
    ctx.save();
    ctx.strokeStyle = deriveWellInk(theme.tokens);
    ctx.globalAlpha = WELL_BORDER_ALPHA;
    const lineWidth = Math.max(2, Math.round(cellPx * 0.08));
    ctx.lineWidth = lineWidth;
    const inset = lineWidth / 2;
    ctx.strokeRect(inset, inset, wellWidthPx - lineWidth, wellHeightPx - lineWidth);
    ctx.restore();
  }

  private paintBoard(theme: Theme, board: GameState['board'], cellPx: number): void {
    for (let y = board.hiddenRows; y < board.height; y++) {
      const row = board.rows[y];
      if (row === undefined) continue;
      const py = (y - board.hiddenRows) * cellPx;
      for (let x = 0; x < board.width; x++) {
        const cell = row[x];
        if (cell === undefined || cell === null) continue;
        const rect: CellRect = { x: x * cellPx, y: py, size: cellPx };
        theme.paintCell(this.ctx, rect, cell, { state: { kind: 'normal' } });
      }
    }
  }

  private paintClearingRows(theme: Theme, frame: RenderFrame, width: number, cellPx: number): void {
    const hiddenRows = frame.state.board.hiddenRows;
    for (const [row, entry] of frame.effects.clearingRows) {
      if (row < hiddenRows) continue;
      const py = (row - hiddenRows) * cellPx;
      for (let x = 0; x < width; x++) {
        const rect: CellRect = { x: x * cellPx, y: py, size: cellPx };
        theme.paintCell(this.ctx, rect, entry.kind, {
          state: { kind: 'clearing', progress: entry.progress },
        });
      }
    }
  }

  private paintGhost(
    theme: Theme,
    state: GameState,
    slot: number,
    hiddenRows: number,
    cellPx: number,
  ): void {
    const piece = state.pieces[slot];
    if (piece === undefined || piece === null) return;

    const toRect = (cell: readonly [number, number]): CellRect | null => {
      const [x, y] = cell;
      if (y < hiddenRows) return null;
      return { x: x * cellPx, y: (y - hiddenRows) * cellPx, size: cellPx };
    };

    const ghostRects = ghostCells(state, slot)
      .map(toRect)
      .filter((r): r is CellRect => r !== null);
    const activeRects = activePieceCells(state, slot)
      .map(toRect)
      .filter((r): r is CellRect => r !== null);
    theme.paintGhost(this.ctx, ghostRects, activeRects, piece.kind);
  }

  private paintActivePiece(
    theme: Theme,
    state: GameState,
    slot: number,
    hiddenRows: number,
    cellPx: number,
    highContrast: boolean,
  ): void {
    const piece = state.pieces[slot];
    if (piece === undefined || piece === null) return;

    for (const [x, y] of activePieceCells(state, slot)) {
      if (y < hiddenRows) continue; // clipped — still in the spawn buffer.
      const rect: CellRect = { x: x * cellPx, y: (y - hiddenRows) * cellPx, size: cellPx };
      theme.paintCell(this.ctx, rect, piece.kind, { state: { kind: 'normal' }, highContrast });
    }
  }

  /**
   * Mark a piece as the *other* player's.
   *
   * Two cues, neither of which is a colour the theme owns:
   *
   * 1. **A diagonal hatch across every cell**, drawn in the theme's own
   *    background token. Background is the one colour guaranteed to contrast
   *    with every piece colour in a theme, because that is what makes the
   *    pieces visible in the first place — so this works in Phosphor, where the
   *    seven pieces differ only in brightness, and in Concrete, where the
   *    stripe *is* the colour-blind mechanism and this reads as its idiom.
   * 2. **A dashed outline around the piece's silhouette**, in the accent, over
   *    a fatter halo in the background colour so the dashes never disappear
   *    into a piece of a similar hue. Interior edges are skipped, so it is one
   *    shape rather than four squares.
   *
   * Nothing here reaches into `packages/themes`: it is chrome the renderer
   * draws, in the same family as the ghost, out of tokens every theme already
   * publishes. BASELINE 4 holds in both directions.
   */
  private paintPartnerMark(
    theme: Theme,
    state: GameState,
    slot: number,
    hiddenRows: number,
    cellPx: number,
  ): void {
    const cells = activePieceCells(state, slot).filter(([, y]) => y >= hiddenRows);
    if (cells.length === 0) return;

    const { ctx } = this;
    const occupied = new Set(
      activePieceCells(state, slot).map(([x, y]) => `${String(x)},${String(y)}`),
    );
    const px = (x: number): number => x * cellPx;
    const py = (y: number): number => (y - hiddenRows) * cellPx;

    ctx.save();

    // 1 — the hatch.
    ctx.globalAlpha = 0.55;
    ctx.strokeStyle = theme.tokens.background;
    ctx.lineWidth = Math.max(1, Math.round(cellPx / 9));
    const step = Math.max(4, Math.round(cellPx / 3));
    for (const [x, y] of cells) {
      const left = px(x);
      const top = py(y);
      ctx.save();
      ctx.beginPath();
      ctx.rect(left, top, cellPx, cellPx);
      ctx.clip();
      ctx.beginPath();
      for (let offset = -cellPx; offset < cellPx * 2; offset += step) {
        ctx.moveTo(left + offset, top);
        ctx.lineTo(left + offset + cellPx, top + cellPx);
      }
      ctx.stroke();
      ctx.restore();
    }

    // 2 — the silhouette, haloed then dashed.
    const outline = (): void => {
      ctx.beginPath();
      for (const [x, y] of cells) {
        const left = px(x);
        const top = py(y);
        const has = (dx: number, dy: number): boolean =>
          occupied.has(`${String(x + dx)},${String(y + dy)}`);
        if (!has(0, -1)) {
          ctx.moveTo(left, top);
          ctx.lineTo(left + cellPx, top);
        }
        if (!has(0, 1)) {
          ctx.moveTo(left, top + cellPx);
          ctx.lineTo(left + cellPx, top + cellPx);
        }
        if (!has(-1, 0)) {
          ctx.moveTo(left, top);
          ctx.lineTo(left, top + cellPx);
        }
        if (!has(1, 0)) {
          ctx.moveTo(left + cellPx, top);
          ctx.lineTo(left + cellPx, top + cellPx);
        }
      }
      ctx.stroke();
    };

    ctx.globalAlpha = 0.9;
    ctx.setLineDash([]);
    ctx.strokeStyle = theme.tokens.background;
    ctx.lineWidth = Math.max(3, Math.round(cellPx / 6));
    outline();

    ctx.globalAlpha = 1;
    ctx.strokeStyle = theme.tokens.accent;
    ctx.lineWidth = Math.max(1.5, Math.round(cellPx / 12));
    ctx.setLineDash([Math.max(3, Math.round(cellPx / 4)), Math.max(2, Math.round(cellPx / 6))]);
    outline();

    ctx.restore();
  }

  /**
   * The reconciliation cue: a fading accent wash over the cells the server just
   * moved this player's piece to.
   *
   * `docs/prompts/06-coop.md`: "Reconciliation never teleports a piece more
   * than one cell without a visible correction cue." The client decides *when*
   * a jump is big enough to be worth explaining (`duel-client.ts`); this is
   * only what it looks like.
   */
  private paintCorrection(frame: RenderFrame, hiddenRows: number, cellPx: number): void {
    const cue = frame.effects.correction;
    if (cue === null || cue === undefined) return;

    const { ctx } = this;
    const fade = 1 - cue.progress;
    ctx.save();
    ctx.strokeStyle = frame.theme.tokens.accent;
    ctx.fillStyle = frame.theme.tokens.accent;
    ctx.lineWidth = Math.max(2, Math.round(cellPx / 8));
    for (const [x, y] of cue.cells) {
      if (y < hiddenRows) continue;
      const left = x * cellPx;
      const top = (y - hiddenRows) * cellPx;
      ctx.globalAlpha = 0.3 * fade;
      ctx.fillRect(left, top, cellPx, cellPx);
      ctx.globalAlpha = 0.9 * fade;
      ctx.strokeRect(left + 1, top + 1, cellPx - 2, cellPx - 2);
    }
    ctx.restore();
  }

  /**
   * The clear-burst particles — `EffectsController`'s pool, already advanced
   * and snapshotted in board cell units; this only ever converts to pixels
   * with the current `cellPx` and paints, the same split every other effect
   * in this file keeps. Clipped to the well so a fast-moving spark cannot
   * paint over the hold/next rail beside it.
   *
   * Shape is the only per-theme branch: `spark`/`ember`/`mote` are filled
   * circles (a mote is simply a bigger, blurrier one — `theme-effects.ts`'s
   * own `maxBlurPx`/`sizeCells` already carry that difference), `shard` is a
   * small flat rectangle — concrete's "cuts instead of fades" reads as a hard
   * edge, not a dot, and blueprint's pencil flecks read the same way.
   */
  private paintParticles(
    frame: RenderFrame,
    hiddenRows: number,
    cellPx: number,
    wellWidthPx: number,
    wellHeightPx: number,
  ): void {
    const particles = frame.effects.particles;
    if (particles === undefined || particles.length === 0) return;

    const { ctx } = this;
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, wellWidthPx, wellHeightPx);
    ctx.clip();

    for (const particle of particles) {
      if (particle.alpha <= 0) continue;
      const px = particle.x * cellPx;
      const py = (particle.y - hiddenRows) * cellPx;
      const size = Math.max(1, particle.sizeCells * cellPx);

      ctx.save();
      ctx.globalAlpha = particle.alpha;
      if (particle.glow && particle.maxBlurPx > 0) {
        ctx.shadowColor = particle.color;
        ctx.shadowBlur = particle.maxBlurPx * particle.alpha;
      }
      ctx.fillStyle = particle.color;

      if (particle.shape === 'shard') {
        ctx.fillRect(px - size / 2, py - size / 4, size, size / 2);
      } else {
        ctx.beginPath();
        ctx.arc(px, py, size / 2, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    }

    ctx.restore();
  }

  /** The quad shockwave ring, or the smaller back-to-back accent pulse — an expanding, fading stroked circle in the theme's own accent. */
  private paintRing(
    ring: RingSnapshot | null | undefined,
    hiddenRows: number,
    cellPx: number,
    wellWidthPx: number,
    wellHeightPx: number,
  ): void {
    if (ring === null || ring === undefined || ring.alpha <= 0 || ring.radiusCells <= 0) return;

    const { ctx } = this;
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, wellWidthPx, wellHeightPx);
    ctx.clip();

    ctx.globalAlpha = ring.alpha;
    ctx.strokeStyle = ring.color;
    ctx.lineWidth = Math.max(1.5, cellPx * 0.12);
    ctx.beginPath();
    ctx.arc(
      ring.x * cellPx,
      (ring.y - hiddenRows) * cellPx,
      ring.radiusCells * cellPx,
      0,
      Math.PI * 2,
    );
    ctx.stroke();

    ctx.restore();
  }

  /**
   * The hold/next rail. The two previews must never be mistaken for each
   * other, so they differ in three ways:
   *
   * 1. **Both slots are labelled** — "HOLD" in the theme's accent, "NEXT" in
   *    its body-text colour — so the two are never the same kind of thing at
   *    a glance, let alone the same piece.
   * 2. **Both get a frame, and the frames are deliberately different**: hold's
   *    is drawn in the accent (dashed once it is spent); next's is drawn in
   *    the theme's own muted `garbage` token. A theme-aware colour pair, not
   *    a hardcoded one — BASELINE 4 and `docs/decisions/0022` both apply to
   *    chrome the renderer draws itself, same as the partner marker.
   * 3. **Hold dims when it cannot be used.** `state.holdAvailable[slot]` is
   *    the engine's own record of "already swapped this piece" (`piece.ts`'s
   *    `holdPiece`); this is the only place the renderer reads it. Dimming
   *    the whole group — label, frame and piece — makes "you cannot hold
   *    again this piece" a property of how the slot *looks*, not something
   *    the player has to remember or trigger and find out the input did
   *    nothing.
   *
   * The next queue gets its own two-part treatment: the piece landing right
   * after this one is drawn at hold's own size, in its own frame — it is the
   * one plan-relevant piece — and the remaining `NEXT_PREVIEW_COUNT - 1`
   * follow it a little smaller (`NEXT_SHRINK`), outside the frame, so it
   * still leads; they are drawn at full brightness, so every upcoming piece
   * reads on the dark board and the light one alike (`docs/decisions/0057`).
   *
   * **HOLD sits below NEXT.** The rail draws the NEXT queue first, starting
   * at the top, and HOLD underneath it at whatever `y` the queue's own height
   * leaves off. The code below reads top to bottom in that order.
   */
  private paintRail(
    theme: Theme,
    state: GameState,
    slot: number,
    wellWidthPx: number,
    cellPx: number,
    /**
     * `curses`' BLIND effect. The "NEXT" label and its frame stay drawn — the
     * slot is still there — but the queue itself is washed out in the same
     * theme-background mechanism `paintFog` and `paintMiniPiece`'s own dim
     * wash use, rather than simply not being drawn: an empty-looking rail
     * would read as a bug, not a curse.
     */
    blindNext = false,
  ): void {
    const { ctx } = this;
    const miniCell = Math.max(4, Math.round(cellPx * MINI_CELL_FRACTION));
    const boxPx = miniCell * MINI_BOX_CELLS;
    const railX = wellWidthPx + Math.round(cellPx * 0.5);
    const labelPx = Math.max(9, Math.round(cellPx * 0.34));
    const framePad = Math.max(2, Math.round(cellPx * 0.12));
    const fg = theme.tokens.foreground ?? DEFAULT_DARK_FOREGROUND;

    ctx.save();
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.font = `700 ${String(labelPx)}px ui-monospace, "Cascadia Code", monospace`;

    // --- NEXT -----------------------------------------------------------
    // NEXT is drawn first, starting at the top of the rail; HOLD follows it,
    // at whatever `y` the queue's own height leaves off (it depends on how
    // many upcoming pieces there are).
    let y = 0;

    ctx.fillStyle = fg;
    ctx.fillText('NEXT', railX, y);
    y += Math.round(labelPx * 1.4);

    ctx.strokeStyle = theme.tokens.garbage;
    ctx.lineWidth = Math.max(1, Math.round(cellPx * 0.05));
    ctx.strokeRect(railX - framePad, y - framePad, boxPx + framePad * 2, boxPx + framePad * 2);
    const nextRegionTop = y - framePad;

    const [firstKind, ...restKinds] = peekNext(state, NEXT_PREVIEW_COUNT);
    if (firstKind !== undefined && !blindNext) {
      this.paintMiniPiece(theme, firstKind, railX, y, miniCell, 0);
    }
    y += boxPx + Math.round(miniCell * 0.5);

    const smallCell = Math.max(3, Math.round(miniCell * NEXT_SHRINK));
    const smallBoxPx = smallCell * MINI_BOX_CELLS;
    const smallOffsetX = Math.round((boxPx - smallBoxPx) / 2);
    for (const kind of restKinds) {
      if (!blindNext) this.paintMiniPiece(theme, kind, railX + smallOffsetX, y, smallCell);
      y += smallBoxPx + Math.round(miniCell * 0.35);
    }

    if (blindNext) {
      ctx.save();
      ctx.globalAlpha = 0.92;
      ctx.fillStyle = theme.tokens.background;
      ctx.fillRect(railX - framePad, nextRegionTop, boxPx + framePad * 2, y - nextRegionTop);
      ctx.restore();
    }

    // The same breathing room the two sections have always had between them
    // — previously the gap after hold's own full-size box, now the gap after
    // the next queue's last (shrunk) entry.
    y += Math.round(labelPx * 1.6);

    // --- HOLD -------------------------------------------------------------
    const holdUsed = state.holdAvailable[slot] === false;
    const hold = state.holds[slot] ?? null;
    const holdDim = holdUsed ? HOLD_USED_DIM : 0;

    ctx.globalAlpha = 1 - holdDim;
    ctx.fillStyle = theme.tokens.accent;
    ctx.fillText('HOLD', railX, y);
    y += Math.round(labelPx * 1.4);

    ctx.strokeStyle = holdUsed ? theme.tokens.garbage : theme.tokens.accent;
    ctx.lineWidth = Math.max(1, Math.round(cellPx * 0.06));
    // A used hold is dashed as well as dimmed — two independent cues, so the
    // state still reads under a high-contrast toggle or a colour-weak theme
    // where "dimmer" alone is a harder signal to catch mid-game.
    ctx.setLineDash(
      holdUsed
        ? [Math.max(2, Math.round(cellPx * 0.2)), Math.max(2, Math.round(cellPx * 0.14))]
        : [],
    );
    ctx.strokeRect(railX - framePad, y - framePad, boxPx + framePad * 2, boxPx + framePad * 2);
    ctx.setLineDash([]);
    ctx.globalAlpha = 1;

    this.paintMiniPiece(theme, hold, railX, y, miniCell, holdDim);

    ctx.restore();
  }

  /**
   * Paint one mini piece, optionally faded toward the theme's own background.
   *
   * `dim` is *not* `ctx.globalAlpha` handed to `theme.paintCell` — several
   * real themes' `paintCell` set `globalAlpha` to an absolute value of their
   * own for a `'normal'`-state cell (`voltage.ts`, `circuit.ts`, `deep.ts`,
   * `blueprint.ts` all do, to drive their own clear animation), which would
   * silently overwrite an ambient alpha set before calling it. A mini piece
   * is painted in `'normal'` state, same as the board, so an ambient alpha
   * cannot be relied on. Painting the piece at full
   * brightness and then washing a translucent rect of `theme.tokens.background`
   * over it afterward dims it regardless of what any theme's `paintCell` did
   * internally, because the wash is this renderer's own draw call, not one
   * routed through the theme.
   */
  private paintMiniPiece(
    theme: Theme,
    kind: PieceKind | null,
    originX: number,
    originY: number,
    miniCell: number,
    dim = 0,
  ): void {
    if (kind === null) return;
    const size = boxSize(kind);
    const offset = ((MINI_BOX_CELLS - size) * miniCell) / 2;
    for (const [x, y] of pieceCells(kind, 0)) {
      const rect: CellRect = {
        x: originX + offset + x * miniCell,
        y: originY + offset + y * miniCell,
        size: miniCell,
      };
      theme.paintCell(this.ctx, rect, kind, { state: { kind: 'normal' } });
    }

    if (dim <= 0) return;
    this.ctx.save();
    this.ctx.globalAlpha = dim;
    this.ctx.fillStyle = theme.tokens.background;
    this.ctx.fillRect(originX, originY, MINI_BOX_CELLS * miniCell, MINI_BOX_CELLS * miniCell);
    this.ctx.restore();
  }
}
